import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { SearchCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import MultiSelectFilterModal, { FilterOption } from "../modals/MultiSelectFilterModal";

const apiUrl = process.env.REACT_APP_API_URL || "";
const SUGG_SIZE = 6;

type UserOption = FilterOption & { code: string };

interface Props {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
}

const UserModalSelect: React.FC<Props> = ({ value, onChange, placeholder }) => {
  const { i18n } = useTranslation();
  const isMK = i18n.language === "mk";
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<UserOption[]>([]);
  const [modalSearch, setModalSearch] = useState("");

  const [inputText, setInputText] = useState("");
  const [editing, setEditing] = useState(false);
  const [showSugg, setShowSugg] = useState(false);
  const [suggIndex, setSuggIndex] = useState(-1);
  const ignoreBlurRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const suppressNextSugg = useRef(false);

  const selectedIds = useMemo(
    () => value.split(",").map((s) => s.trim()).filter(Boolean),
    [value],
  );

  const filteredOptions = useMemo(() => {
    const q = modalSearch.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) => o.code.toLowerCase().includes(q) || o.name.toLowerCase().includes(q),
    );
  }, [options, modalSearch]);

  useEffect(() => {
    if (options.length > 0) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get<Array<{ _id: string; userName?: string; firstName?: string; lastName?: string }>>(
        `${apiUrl}/api/users/for-criteria`,
        { headers: { "x-auth-token": localStorage.getItem("token") || "" } },
      )
      .then((res) => {
        if (cancelled) return;
        setOptions(
          (Array.isArray(res.data) ? res.data : []).map((u) => ({
            _id: u._id,
            code: u.userName || "",
            name: ((u.firstName || "") + " " + (u.lastName || "")).trim(),
          })),
        );
      })
      .catch(() => { if (!cancelled) setOptions([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const lastToken = editing ? (inputText.split(",").pop()?.trim().toLowerCase() ?? "") : "";

  const suggestions = useMemo<UserOption[]>(() => {
    if (!lastToken || !editing) return [];
    return options
      .filter((o) => o.code.toLowerCase().includes(lastToken) || o.name.toLowerCase().includes(lastToken))
      .slice(0, SUGG_SIZE);
  }, [lastToken, options, editing]);

  useEffect(() => {
    if (suppressNextSugg.current) { suppressNextSugg.current = false; setShowSugg(false); setSuggIndex(-1); return; }
    setShowSugg(suggestions.length > 0 && editing && lastToken.length > 0);
    setSuggIndex(-1);
  }, [suggestions.length, editing, lastToken]);

  const selectedNames = selectedIds
    .map((id) => {
      const option = options.find((o) => o._id === id);
      return option?.name || option?.code;
    })
    .filter(Boolean)
    .join(", ");

  useEffect(() => {
    if (!editing) setInputText(selectedNames);
  }, [selectedNames, editing]);

  const selectSuggestion = (opt: UserOption) => {
    suppressNextSugg.current = true;
    const parts = inputText.split(",").map((s) => s.trim());
    parts[parts.length - 1] = opt.name || opt.code;
    const newText = parts.join(", ");
    setInputText(newText);
    onChange(Array.from(new Set([...selectedIds, opt._id])).join(","));
    setShowSugg(false);
    setSuggIndex(-1);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const commitTypedText = () => {
    setEditing(false);
    setShowSugg(false);
    setSuggIndex(-1);
    const tokens = Array.from(
      new Set(inputText.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean)),
    );
    if (tokens.length === 0) { onChange(""); return; }
    const resolved = tokens
      .map((tok) => options.find((o) => o.code.toLowerCase() === tok || o.name.toLowerCase() === tok))
      .filter((o): o is UserOption => !!o);
    if (resolved.length === 0) return;
    onChange(Array.from(new Set(resolved.map((o) => o._id))).join(","));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (showSugg && suggestions.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setSuggIndex((i) => Math.min(i + 1, suggestions.length - 1)); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setSuggIndex((i) => Math.max(i - 1, -1)); return; }
      if (e.key === "Enter") { e.preventDefault(); selectSuggestion(suggestions[suggIndex >= 0 ? suggIndex : 0]); return; }
      if (e.key === "Escape") { e.preventDefault(); setShowSugg(false); return; }
    }
    if (e.key === "Enter") { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
  };

  return (
    <>
      <div className="relative w-full">
        <input
          ref={inputRef}
          type="text"
          value={inputText}
          placeholder={placeholder || (isMK ? "Референт" : "Referent")}
          onFocus={() => { setEditing(true); setInputText(selectedNames); }}
          onChange={(e) => setInputText(e.target.value)}
          onBlur={() => { if (!ignoreBlurRef.current) commitTypedText(); }}
          onKeyDown={handleKeyDown}
          className="h-9 w-full rounded-md border border-gray-300 bg-white pl-3 pr-9 text-left text-xs text-gray-800 shadow-sm transition-all placeholder:text-gray-400 hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
        />
        <button
          type="button"
          onClick={() => {
            setModalSearch("");
            setOpen(true);
          }}
          aria-label={isMK ? "Отвори избор на референт" : "Open referent selector"}
          className="absolute inset-y-0 right-0 flex w-8 items-center justify-center rounded-r-md text-gray-400 hover:text-blue-600 focus:outline-none"
        >
          <SearchCheck size={15} className="flex-shrink-0" />
        </button>

        {showSugg && (
          <ul
            onMouseDown={() => { ignoreBlurRef.current = true; }}
            onMouseUp={() => { ignoreBlurRef.current = false; }}
            className="absolute z-50 mt-0.5 w-full rounded-md border border-gray-200 bg-white shadow-lg overflow-hidden"
          >
            {suggestions.map((opt, i) => (
              <li
                key={opt._id}
                onMouseDown={() => selectSuggestion(opt)}
                className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-xs transition-colors ${
                  i === suggIndex ? "bg-blue-50 text-blue-800" : "hover:bg-gray-50 text-gray-800"
                }`}
              >
                <span className="text-gray-800 truncate">{opt.name}</span>
                <span className="font-mono font-semibold shrink-0 text-gray-500 ml-auto">{opt.code}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <MultiSelectFilterModal
        isOpen={open}
        onClose={() => setOpen(false)}
        onApply={(ids) => onChange(ids.join(","))}
        title={placeholder || (isMK ? "Избери референт" : "Select referent")}
        options={filteredOptions}
        selectedIds={selectedIds}
        loading={loading}
        searchValue={modalSearch}
        onSearchChange={setModalSearch}
      />
    </>
  );
};

export default UserModalSelect;
